import React from 'react';
import { ArrowPathIcon, CalendarDaysIcon, AcademicCapIcon, TrophyIcon, UserGroupIcon, ExclamationTriangleIcon, ChartBarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage6 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 6: Embed it, and Manage it like a Product" icon={<ArrowPathIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Embed performance into 1:1s, goals, growth conversations, compensation, and recognition</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Create clear processes for onboarding, underperformance, and calibrations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Run reflective exercises after each cycle to continuously improve the framework</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Treat performance as a practice, not a process—keep it connected to business purpose</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Congrats! Now the Hard Work Starts</h3>
        <p className="text-gray-300 mb-6">
          You've just designed, launched, and executed the first cycle of your new performance framework. Now the hard work starts!
        </p>

        <p className="text-gray-300 mb-6">
          You have a few jobs to think about now, including:
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2">1. Making This Sticky Long Term</h4>
            <p className="text-gray-300 text-sm">How to embed performance across your organization so it becomes natural</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
            <h4 className="text-white font-semibold mb-2">2. Managing This Long Term</h4>
            <p className="text-gray-300 text-sm">How to continue knowing how well this framework is actually working for you</p>
          </div>
        </div>

        <p className="text-gray-300">
          This is all about how we embed performance across your organisation. Let's dive into a few things you could do to help here…
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <CalendarDaysIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">1:1s</h3>
        </div>
        <p className="text-gray-300 mb-6">
          The foundation of continuous performance conversations.
        </p>

        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Documented guidance on running effective 1:1s for everyone to see</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Simple, but solid agendas to keep conversations focused</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">Space dedicated in every 1:1 to touch on performance in a light-weight way</span>
          </li>
        </ul>

        <ExpandableCallout type="tip" title="Traffic Light 1:1s" className="mb-4">
          <p className="text-gray-300 mb-3">
            If you're using a traffic-light type system like the one we outlined in Stage 4, there's a perfect opportunity 
            to replicate a lighter version of this to run regularly in 1:1s to help build some continuity between cycles.
          </p>
          <div className="bg-gray-800 p-4 rounded border border-gray-700">
            <p className="text-gray-300 text-sm mb-2"><strong className="text-white">Simple 1:1 Check-in:</strong></p>
            <p className="text-gray-300 text-sm">"How are you feeling about your performance this month on Results, Execution, and Behaviours? Any areas to discuss?"</p>
          </div>
        </ExpandableCallout>

        <p className="text-gray-300 text-sm">
          We have some guidance & examples you might like on our <a href="https://www.notion.so/Open-Org-s-Performance-Playbook-Template-1b3f015e5d468046bdceffe4cf92200c?pvs=21" 
          className="text-primary hover:text-primary-light underline" target="_blank" rel="noopener noreferrer">performance handbook template</a>.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Goals</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              <strong className="text-white">Clear, visible goals</strong> to help folks understand exactly what the business needs to achieve
            </span>
            <div className="ml-6 mt-2 text-gray-300 text-sm">
              You should reinforce this in Town Halls, OKRs, & business updates
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              <strong className="text-white">Clarity on individual goals</strong> for everyone in the business at any given moment in time
            </span>
            <div className="ml-6 mt-2 text-gray-300 text-sm">
              Managers should help make sure everyone documents goals & that they connect to & impact business goals
            </div>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Growth & Compensation</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-4 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <div>
              <span className="text-gray-300">
                <strong className="text-white">Documented how performance outcomes impact growth & compensation</strong>
              </span>
              <div className="ml-0 mt-2 text-gray-300 text-sm">
                You can bake these into things like PDPs & career conversations with managers
              </div>
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <div>
              <span className="text-gray-300">
                <strong className="text-white">Career & Compensation Frameworks</strong> that show clear connection & relationship to performance
              </span>
              <div className="ml-0 mt-2 text-gray-300 text-sm">
                Compensation philosophies & career frameworks are important to get in place if you don't have these already
              </div>
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <div>
              <span className="text-gray-300">
                <strong className="text-white">Accountability on decisions you make</strong>
              </span>
              <div className="ml-0 mt-2 text-gray-300 text-sm">
                Back up decisions you make on promotions & pay rises. Be as public as you can. Make sure you can tie these clearly to performance (if that's the goal of your performance framework). Actions &gt; words.
              </div>
            </div>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Onboarding</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <p className="text-gray-300 mb-4">
          Thought about how you might help someone climatise to your performance practices from Day 1.
        </p>

        <p className="text-gray-300 mb-6">
          Often new hires aren't included in a particular cycle if they happen to join too close to one. You could pilot a 
          light-weight probationary version of your framework though that managers could follow with new hires. Not a formal 
          assessment, but an opportunity to have the right conversations with new hires and assimilate them to what's coming.
        </p>

        <p className="text-gray-300 text-sm">
          Think about how you might make that experience during onboarding & ramp up as seamless as possible.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <TrophyIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Recognition</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              <strong className="text-white">Intentional space & opportunity to recognise folks who are performing</strong>
            </span>
            <div className="ml-6 mt-2 text-gray-300 text-sm">
              Team meetings, Slack updates, Town Halls are all great places to publicly recognise people. Try to think about how you might clearly connect the dots for folks so they can see exactly <strong>how and why</strong> someone is being recognised as a high performer. Make it obvious to people. Reference your performance framework.
            </div>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Underperformance</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-4 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <div>
              <span className="text-gray-300">
                <strong className="text-white">Clarity on what underperformance looks like</strong>
              </span>
              <div className="ml-0 mt-2 text-gray-300 text-sm">
                Documented guidance in handbooks is helpful here. Like the page on our template 
                <a href="https://www.notion.so/Performance-Recovery-at-company-1b3f015e5d4681e9b843e12517d91814?pvs=21" 
                   className="text-primary hover:text-primary-light underline ml-1" target="_blank" rel="noopener noreferrer">here</a>
              </div>
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <div>
              <span className="text-gray-300">
                <strong className="text-white">Ability to stay accountable to what you have written down</strong>
              </span>
              <div className="ml-0 mt-2 text-gray-300 text-sm">
                If behaviours or results slip, you need to stay consistent in how you respond. Make sure anyone on a performance recovery plan has 100% honesty & clarity on why they are on one. If anyone exits the business due to underperformance, make sure you are open & honest with the rest of the business about why (where legally possible of course). This is not about naming & shaming, or making an example of people, it's about staying accountable and embedding standards.
              </div>
            </div>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Calibrations</h3>
        </div>
        
        <h4 className="text-lg font-semibold text-white mb-4">Make sure you have…</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">
              <strong className="text-white">Consistency across the business on how folks are applying the framework</strong>
            </span>
            <div className="ml-6 mt-2 text-gray-300 text-sm">
              Calibrations can be a helpful ritual if you have noticeable differences across teams on ratings and outcomes, 
              or perhaps have junior managers who are newer to performance assessment too.
            </div>
          </li>
        </ul>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <ArrowPathIcon className="h-7 w-7 text-primary-light mr-3" />
          <h3 className="text-2xl font-semibold text-white">Continuous Improvement: Manage it like a Product</h3>
        </div>
        <p className="text-gray-300 mb-6">
          How do you know that your performance framework's still working for you in 6, 12 or 24 months time? Startups change rapidly. 
          The systems you put in place need to change along the way too.
        </p>

        <p className="text-gray-300 mb-6">
          We'd actually recommend running a reflective exercise after each cycle you run to help validate whether your approach 
          is still fit for purpose, or needs iterating on before the next cycle.
        </p>

        <CopyToNotion title="Post-Cycle Reflection Checklist">
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-white mb-3">Performance Framework Health Check</h4>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-white mb-2">Business Impact</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Is this tangibly helping us perform as a business? (How do we know that)</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Are we impacting the things that we wanted to impact (e.g. growth, retention, outperformance)</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Is this causing us any friction across the business? (If so where, why, how much?)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">Foundation Review</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm mb-2">If you're cloudy on the above, revisit:</p>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Why do we do this?</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">What are we assessing (pillars) & why?</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">What will performance outcomes influence? (e.g. growth, reward..)</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Do we still have the right people in the right roles?</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Do those people have clarity on roles & responsibilities</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Is there still clarity on goals & ways of work expectations</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">Is there still clarity on values & behaviours we do & do not expect to see here</span>
                      </div>
                      <div className="flex items-start">
                        <input type="checkbox" className="mt-1 mr-3 h-4 w-4" />
                        <span className="text-gray-300 text-sm">What feedback have we had from people here on performance practices?</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CopyToNotion>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">Critical Reminder</h4>
          <p className="text-gray-300 text-sm mb-3">
            This should not (& never will) be a static framework. The moment you stop remembering that performance measurement 
            has a <strong className="text-white">purpose</strong> in your org, it becomes a process, not a practice.
          </p>
          <p className="text-gray-300 text-sm">
            That purpose is typically to impact bottom-line & business results, and maximise return on the investment you make 
            in your people. You need to make sure you are regularly reviewing just how good a job this is doing at influencing that.
          </p>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Made a good start on embedding performance into your business so it feels more like a practice than a process</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Clear processes for 1:1s, recognition, underperformance, and calibrations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">A system for continuous improvement and framework evolution</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Performance practices that are connected to business purpose and outcomes</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Important:</strong> There is no final, finished outcome here. This will be an ongoing piece of 
              management for you to keep working on over time. Performance is a practice that evolves with your organization.
            </p>
          </div>
        </div>
      </PlaybookCard>

     
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage6; 